import { useTranslation } from "react-i18next";
import { Button, cn, Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@tellescope/ui";
import { formatPhone } from "@tellescope/utils";
import type { TwilioPhoneNumber } from "@tellescope/tanstack-query";
import { CapabilityBadges } from "./CapabilityBadges";

export interface YourPhoneNumbersViewProps {
  isAdmin: boolean;
  isLoading: boolean;
  purchasedNumbers: TwilioPhoneNumber[];
  onStartVerification: (number: TwilioPhoneNumber) => void;
  onBuyAnother: () => void;
}

export const YourPhoneNumbersView = ({
  isAdmin,
  isLoading,
  purchasedNumbers,
  onStartVerification,
  onBuyAnother,
}: YourPhoneNumbersViewProps) => {
  const { t } = useTranslation("onboarding");

  return (
    <div className="min-h-0 pt-6 pb-4 flex flex-col gap-6 justify-center">
      <h3 className="text-xl font-semibold text-gray-900 leading-none">{t("Your Phone Numbers")}</h3>
      <div className="flex max-h-full flex-col overflow-hidden rounded-md border">
        <div className="flex-none overflow-hidden bg-gray-50">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-[20%] py-4 px-6 uppercase text-xs text-muted-foreground">
                  {isLoading ? <Skeleton className="h-3 w-32 bg-gray-300" /> : t("Phone Number")}
                </TableHead>
                <TableHead className="w-[12%] py-4 px-6 uppercase text-xs text-muted-foreground">
                  {isLoading ? <Skeleton className="h-3 w-24 bg-gray-300" /> : t("Type")}
                </TableHead>
                <TableHead className="w-[24%] py-4 px-6 uppercase text-xs text-muted-foreground">
                  {isLoading ? <Skeleton className="h-3 w-24 bg-gray-300" /> : t("Capabilities")}
                </TableHead>
                <TableHead className="w-[22%] py-4 px-6 uppercase text-xs text-muted-foreground">
                  {isLoading ? <Skeleton className="h-3 w-24 bg-gray-300" /> : t("Messaging Service")}
                </TableHead>
                <TableHead className="w-[22%] py-4 px-6 uppercase text-xs text-muted-foreground">
                  {isLoading ? <Skeleton className="h-3 w-24 bg-gray-300" /> : t("TFN Verification")}
                </TableHead>
              </TableRow>
            </TableHeader>
          </Table>
        </div>
        <div className="min-h-0 overflow-y-auto bg-white">
          <Table className="table-fixed">
            <TableBody>
              {isLoading
                ? Array.from({ length: 8 }, (_, index) => (
                    <TableRow key={index} className="bg-white">
                      <TableCell className="w-[20%] py-4 px-6">
                        <Skeleton className="h-4 w-44 bg-gray-300" />
                      </TableCell>
                      <TableCell className="w-[12%] py-4 px-6">
                        <Skeleton className="h-4 w-44 bg-gray-300" />
                      </TableCell>
                      <TableCell className="w-[24%] py-4 px-6">
                        <Skeleton className="h-4 w-44 bg-gray-300" />
                      </TableCell>
                      <TableCell className="w-[22%] py-4 px-6">
                        <Skeleton className="h-4 w-24 bg-gray-300" />
                      </TableCell>
                      <TableCell className="w-[22%] py-4 px-6">
                        <Skeleton className="h-4 w-24 bg-gray-300" />
                      </TableCell>
                    </TableRow>
                  ))
                : purchasedNumbers.map((number) => (
                    <TableRow key={number.phoneNumber + number.sid} className="bg-white hover:bg-white">
                      <TableCell className="w-[20%] py-4 px-6">{formatPhone(number.phoneNumber)}</TableCell>
                      <TableCell className="w-[12%] py-4 px-6">
                        {number.type === "TFN" ? t("Toll-Free") : t("10DLC")}
                      </TableCell>
                      <TableCell className="w-[24%] py-4 px-6 space-x-1">
                        <CapabilityBadges capabilities={number.capabilities} idPrefix={number.phoneNumber} />
                      </TableCell>
                      <TableCell className={cn("w-[22%] py-4 px-6 text-muted-foreground truncate")}>
                        {number.isInMessagingService ? t("Default Service") : "-"}
                      </TableCell>
                      <TableCell className={cn("w-[22%] py-4 px-6 text-muted-foreground")}>
                        {number.tfnVerificationStatus === "not_started" ? (
                          <Button variant="outline" disabled={!isAdmin} onClick={() => onStartVerification(number)}>
                            {t("Verify")}
                          </Button>
                        ) : number.tfnVerificationStatus === "rejected" ? (
                          <div className="flex items-center gap-3">
                            <span className="text-destructive">{t("Rejected")}</span>
                            <Button variant="outline" disabled={!isAdmin} onClick={() => onStartVerification(number)}>
                              {t("Retry")}
                            </Button>
                          </div>
                        ) : number.tfnVerificationStatus === "not_required" ? (
                          t("Not Required")
                        ) : number.tfnVerificationStatus === "pending" ||
                          number.tfnVerificationStatus === "in_review" ? (
                          t("Processing")
                        ) : number.tfnVerificationStatus === "verified" ? (
                          t("Verified")
                        ) : (
                          t("Unknown")
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              <TableRow className="bg-gray-50">
                <TableCell colSpan={5} className="w-[100%] py-4 px-6">
                  {isLoading ? (
                    <Skeleton className="h-4 w-44 bg-gray-300" />
                  ) : (
                    <Button onClick={onBuyAnother}>{t("Buy another number")}</Button>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};
